// Pure view-model for the "Your swims" full-month calendar.
//
// Built ONLY from the workouts store (WorkoutSummary). NEVER touches the
// per-swim firehose (stroke/HR) — see the Firehose Trap in the plan.
//
// All date math is LOCAL time, matching aggregates.ts. A swim at 11:55 PM
// local belongs to that local day. Week starts Monday (ISO 8601).

import type { WorkoutSummary } from "@contract/types";

export interface CalendarSwim {
  uuid: string;
  startDate: string; // ISO-8601, used for time-of-day display + intra-day sort
  distanceMeters: number | null;
  // True ONLY when the workout summary explicitly says open water. nil/absent/
  // "pool"/"unknown" all read false — never guessed from distance or any other
  // field (spec §4-V1.5: the list badge trusts swimmingLocationType alone).
  isOpenWater: boolean;
}

export interface CalendarDay {
  dayOfMonth: number; // 1..31
  weekdayIndex: number; // 0=Mon .. 6=Sun
  swims: CalendarSwim[]; // ascending by startDate; [] = rest day
  isToday: boolean;
  isFuture: boolean;
  date: Date; // local midnight of this day
  swimmers?: number; // Club calendar only (clubCalendar.ts): distinct swimmers that day
}

export interface CalendarMonth {
  year: number;
  monthIndex: number; // 0=Jan .. 11=Dec
  label: string; // "June 2026"
  leadingBlanks: number; // empty cells before day 1 (Monday-first grid)
  days: CalendarDay[];
  swimCount: number;
  totalDistanceMeters: number;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Monday-first weekday index for a Date: 0=Mon .. 6=Sun. */
function mondayIndex(d: Date): number {
  const day = d.getDay(); // 0=Sun .. 6=Sat
  return day === 0 ? 6 : day - 1;
}

/** Local year-month-day key, e.g. "2026-5-10". Local time by construction. */
function localDayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function buildSwimCalendar(
  workouts: WorkoutSummary[],
  now: Date = new Date(),
): CalendarMonth[] {
  // 1. Group swims by local calendar day.
  const byDay = new Map<string, CalendarSwim[]>();
  let earliest: Date | null = null;
  for (const w of workouts) {
    const start = new Date(w.startDate);
    if (Number.isNaN(start.getTime())) continue; // skip unparseable
    const key = localDayKey(start);
    const cs: CalendarSwim = {
      uuid: w.uuid,
      startDate: w.startDate,
      distanceMeters: w.totalDistance?.value ?? null,
      isOpenWater: w.swimmingLocationType === "openWater",
    };
    const arr = byDay.get(key);
    if (arr) arr.push(cs);
    else byDay.set(key, [cs]);
    if (earliest === null || start < earliest) earliest = start;
  }
  for (const arr of byDay.values()) {
    arr.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }

  // 2. Month range: earliest swim's month .. now's month (inclusive). Oldest first.
  const rangeStart = earliest
    ? new Date(earliest.getFullYear(), earliest.getMonth(), 1)
    : new Date(now.getFullYear(), now.getMonth(), 1);
  const rangeEndYear = now.getFullYear();
  const rangeEndMonth = now.getMonth();

  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayKey = localDayKey(todayMidnight);

  const months: CalendarMonth[] = [];
  const cursor = new Date(rangeStart);
  while (
    cursor.getFullYear() < rangeEndYear ||
    (cursor.getFullYear() === rangeEndYear && cursor.getMonth() <= rangeEndMonth)
  ) {
    const year = cursor.getFullYear();
    const monthIndex = cursor.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const days: CalendarDay[] = [];
    let swimCount = 0;
    let totalDistanceMeters = 0;
    for (let dom = 1; dom <= daysInMonth; dom++) {
      const date = new Date(year, monthIndex, dom);
      const swims = byDay.get(localDayKey(date)) ?? [];
      swimCount += swims.length;
      for (const s of swims) {
        if (s.distanceMeters != null) totalDistanceMeters += s.distanceMeters;
      }
      days.push({
        dayOfMonth: dom,
        weekdayIndex: mondayIndex(date),
        swims,
        isToday: localDayKey(date) === todayKey,
        isFuture: date.getTime() > todayMidnight.getTime(),
        date,
      });
    }

    months.push({
      year,
      monthIndex,
      label: `${MONTH_NAMES[monthIndex]} ${year}`,
      leadingBlanks: mondayIndex(new Date(year, monthIndex, 1)),
      days,
      swimCount,
      totalDistanceMeters,
    });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
}

export function formatMonthSummary(month: CalendarMonth): string {
  if (month.swimCount === 0) return "No swims";
  const word = month.swimCount === 1 ? "swim" : "swims";
  const km = (month.totalDistanceMeters / 1000).toFixed(1);
  return `${month.swimCount} ${word} · ${km} km`;
}
